import { Package, Truck, BarChart3, Users, MapPin, Phone, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBpbmR1c3RyaWFsJTIwc3RvcmFnZXxlbnwwfHx8fDE3NzI1MzIwNjZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Lagerlokal"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <div className="flex items-center gap-4 mb-6">
            <Package className="h-16 w-16 text-primary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold font-heading text-white mb-4">
            Gränsbygdens Lagersystem
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-2xl">
            Effektiv lagerhantering för Sveriges ledande vitvarudistributör
          </p>
          
          {/* Link to webshop */}
          <a 
            href="https://www.gransbygden.se" 
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-8"
          >
            <Button size="lg" className="gap-2 text-lg px-8 py-6 bg-primary hover:bg-primary/90">
              <ExternalLink className="h-5 w-5" />
              Handla vitvaror på gransbygden.se
            </Button>
          </a>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-16">
        {/* About */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-3xl font-bold font-heading mb-6 text-primary">
            Om Gränsbygdens Köpcentrum
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Sedan 1985 har Gränsbygdens Köpcentrum varit en pålitlig partner för vitvaror och hushållsprodukter 
            i Småland. Från vårt lager i Markaryd levererar vi snabbt och säkert till återförsäljare 
            och slutkunder i hela regionen.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Med över 1000 unika produkter i lager och dagliga leveranser via Bring och DSV, 
            ser vi till att rätt produkt når rätt plats i rätt tid.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <Card className="bg-card border-primary/20">
            <CardContent className="p-6 text-center">
              <Package className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-primary mb-1">1000+</p>
              <p className="text-sm text-muted-foreground">Produkter i lager</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-primary/20">
            <CardContent className="p-6 text-center">
              <Truck className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-primary mb-1">50+</p>
              <p className="text-sm text-muted-foreground">Leveranser per dag</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-primary/20">
            <CardContent className="p-6 text-center">
              <Users className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-primary mb-1">200+</p>
              <p className="text-sm text-muted-foreground">Nöjda kunder</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-primary/20">
            <CardContent className="p-6 text-center">
              <BarChart3 className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold text-primary mb-1">38 år</p>
              <p className="text-sm text-muted-foreground">I branschen</p>
            </CardContent>
          </Card>
        </div>

        {/* Fun Facts */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold font-heading mb-6 text-center">
            Visste du att...
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-lg bg-muted/30 border border-border">
              <p className="text-muted-foreground">
                Vårt lager rymmer ungefär lika många kylskåp som det finns invånare i en liten by? 
                Om vi ställde alla kylskåp på rad skulle de sträcka sig nästan 2 kilometer!
              </p>
            </div>
            <div className="p-6 rounded-lg bg-muted/30 border border-border">
              <p className="text-muted-foreground">
                Varje år hanterar vi över 10 000 leveranser - det är nästan 30 leveranser varje dag, 
                året runt, oavsett om det är -30°C eller +30°C utomhus!
              </p>
            </div>
            <div className="p-6 rounded-lg bg-muted/30 border border-border">
              <p className="text-muted-foreground">
                Vår äldsta produkt i sortimentet har funnits hos oss sedan 1992 - 
                en klassisk diskmaskin som fortfarande är en av våra bästsäljare!
              </p>
            </div>
            <div className="p-6 rounded-lg bg-muted/30 border border-border">
              <p className="text-muted-foreground">
                Om man räknar alla streckkoder vi skannar på ett år skulle siffran bli 
                över 100 000 - det är mer än 11 skanningar i timmen, dygnet runt!
              </p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
            <MapPin className="h-5 w-5" />
            <span>Markaryd, Småland</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Phone className="h-5 w-5" />
            <span>Kontakta oss för mer information</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2024 Gränsbygdens. Alla rättigheter förbehållna.</p>
        </div>
      </footer>
    </div>
  );
}
